<template>
  <div
    :data-id="dataId"
    :data-group="dataGroup"
    class="input"
    :class="[
      inputTypeClass,
      {'preview-only': isPreviewing},
      {'is-active': value},
      {'is-linking': isLinking}]">

    <Notification
      v-if="notification.active"
      :content="notification.content"
      :type="notification.type"/>

    <div class="field-label" v-if="!isCheckbox">
      <label :for="_uid">{{ label }}</label>
      <div v-if="helpText" class="field-help">
        <sprite name="help" />
        <tooltip class="field-helper-text" :content="helpText" />
      </div>
    </div>

    <div class="input-wrapper">
      <span v-if="preText">{{ preText }}</span>
      <input
        v-if="!special"
        :id="_uid"
        :type="type"
        @input="handleChange($event)"
        :placeholder="placeholder"
        v-model="resource"
      />
    </div>

    <div class="input-wrapper" v-if="isCheckbox">
      <span v-if="preText">{{ preText }}</span>
      <input
        :id="_uid"
        type="checkbox"
        @change="handleChange($event)"
        :placeholder="placeholder"
        v-model="resource"
      />
      <label :for="_uid">{{ label }}</label>
    </div>

    <div v-if="isLink">
      <div class="input-wrapper" >
        <span v-if="preText">{{ preText }}</span>
        <input
          :id="_uid"
          @input="handleChange($event)"
          :placeholder="placeholder"
          v-model="resource"
        />

        <button
          @click.prevent="getPages"
          class="btn btn-page-search is-subtle">
          My Pages
        </button>
      </div>

      <transition name="fade" mode="in-out">
        <div class="site-page-list" v-if="pages.length">
          <p>Select a page to link this item to:</p>
          <div v-for="page in pages" :key="page.path">
            <span @click="setPage(page.path, $event)">{{page.name}}</span>
          </div>
          <hr>
          <p>Remember to add the <strong>http</strong> or <strong>https</strong> protocol at the beginning of the website address if you'd like to link to an exteral website. E.g. <strong>https://google.com/</strong></p>
        </div>
      </transition>
    </div>

    <div class="textarea-wrapper" v-if="isCode">
      <textarea
        class="code-textarea"
        v-model="resource"
        :placeholder="placeholder"
        @input="handleChange($event)">
      </textarea>
    </div>

    <div class="textarea-wrapper" v-if="isTextArea">
      <div
        class="btn is-tertiary btn-source"
        :class="{'is-active': sourceCodeActive}"
        v-if="!disableEditor && isAdmin"
        @click="toggleSourceCode"
      >
        Source
      </div>

      <textarea
        v-if="showTextArea"
        v-model="resource"
        :placeholder="placeholder"
        @input="handleChange($event)"
      ></textarea>
      <text-editor
        v-if="showTextEditor"
        :allowImages="allowImages"
        v-model="resource"
        :id="_uid"
        @input="handleChange"
      />
    </div>

    <select
      v-if="type === 'select'"
      :id="_uid"
      v-model="resource"
      @change="handleChange"
    >
      <option value="">Choose one</option>

      <option
        v-for="option in options"
        :key="option.key"
        :value="option.value"
        :selected="option.value === resource"
      >
        {{ option.key }}
      </option>
    </select>
    <error :error="error"></error>
  </div>
</template>

<script>
import {mapState} from 'vuex'

import input from '@/mixins/input'
import notification from '@/mixins/notification'

import Error from '@/components/Error'
import Sprite from '@/components/Sprite'
import Tooltip from '@/components/Tooltip'
import TextEditor from '@/components/TextEditor'

export default {
  name: 'Field',

  mixins: [input, notification],

  props: {
    label: String,
    error: String,
    placeholder: String,
    helpText: String,
    preText: String,
    allowImages: Boolean,

    pages: {
      type: Array,
      required: false,
      description: 'This shows all site pages and allows some wigets to link to them'
    },

    image: {
      type: String,
      required: false
    },

    isPreviewing: {
      type: Boolean,
      required: false,
      description: 'This turns off some styling for the widget previewer fields'
    },

    isLinking: {
      type: Boolean,
      required: false,
      description: 'This determinds if the field hides/shows other fields'
    },

    dataGroup: {
      type: String,
      required: false,
      description: 'This allows us to toggle the a group of fields from the `isLinking` element'
    },

    dataId: {
      type: String,
      required: false,
      description: 'This allows us to toggle the field from the `isLinking` element'
    },

    disableEditor: {
      type: Boolean,
      default: false
    },

    type: {
      type: String,
      default: 'text'
    },

    options: {
      type: Array
    },

    value: {
      required: false
    }
  },

  data() {
    return {
      editor: null,
      sourceCodeActive: false
    }
  },

  components: {
    Error,
    TextEditor,
    Sprite,
    Tooltip
  },

  computed: {
    ...mapState(['currentUser']),

    special() {
      return ['code', 'textarea', 'select', 'checkbox', 'link'].includes(this.type)
    },

    inputTypeClass() {
      return `is-${this.type}`
    },

    isAdmin() {
      return this.currentUser.admin
    },

    isTextArea() {
      return this.type === 'textarea'
    },

    isCode() {
      return this.type === 'code'
    },

    isLink() {
      return this.type === 'link'
    },

    isCheckbox() {
      return this.type === 'checkbox'
    },

    showTextArea() {
      return this.disableEditor || this.sourceCodeActive
    },

    showTextEditor() {
      return !this.disableEditor && !this.sourceCodeActive
    }
  },

  methods: {
    toggleSourceCode() {
      this.sourceCodeActive = !this.sourceCodeActive
    },

    getPages() {
      this.$emit('getPages')
    },

    setPage(slug, event) {
      this.value = slug
      this.resource = slug
      this.handleChange(event)
    },

    handleChange(event) {
      this.$emit('handleChange', event)
    }
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/base/theme'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

.input
  margin-bottom: $gap-large
  position: relative
  width: 100%

  .field-label
    display: flex
    align-items: center
    margin-bottom: 5px

    .field-help
      margin-left: $gap-x-small
      height: 16px
      cursor: help

      &:hover
        .field-helper-text
          opacity: 1
          pointer-events: all

    .sprite
      opacity: 0.2
      width: 15px
      height: 15px

      @include themify($themes)
        fill: themed('color-body')

  label
    display: block
    font-size: 12px
    font-family: $font-bold
    text-transform: uppercase

  input, textarea, button
    width: 100%
    padding: 8px 5px

    &:focus
      outline: none

  input, textarea
    border: 1px solid
    border-radius: 3px
    border-color: $color-border

  textarea
    min-height: 100px
    resize: none

    &.code-textarea
      font-family: "courier new"
      height: 400px
      resize: vertical

  input
    &[type=color]
      height: 50px

  &:not(.preview-only).is-checkbox
    display: flex
    align-items: center

    .error
      position: absolute
      left: 0
      top: 16px

    input
      visibility: hidden
      margin: 0

      &:checked
        opacity: 0.1

        + label
          &:before
            @include themify($themes)
              background: themed('color-primary')

          &:after
            transform: translate(-3px, 2px)

    label
      margin-bottom: 0

      &:before, &:after
        content: ''
        transition: all .2s ease-in-out
        position: absolute
        right: -65px
        top: -5px
        border-radius: 50px

      &:before
        cursor: pointer
        width: 50px
        height: 25px
        background: $color-border

      &:after
        background: rgba(0, 0, 0, 0.25)
        background-position: 50% 50%
        background-size: cover
        width: 20px
        height: 20px
        pointer-events: none
        transform: translate(-27px, 2px)

    input
      flex: 1

  .input-wrapper
    display: flex
    align-items: center

    span
      margin-right: $gap-x-small
      white-space: nowrap

  .textarea-wrapper
    position: relative
    background: #fff

  .btn-source
    position: absolute
    top: -30px
    right: 0
    font-size: 7px
    padding: 5px
    opacity: 0.5
    transition: opacity .2s ease-in-out

    &.is-active
      opacity: 1

  .btn-page-search
    margin-left: $gap-small
    max-width: 100px

  .site-page-list
    padding: $gap-small
    margin-top: $gap-small
    background: $color-code-background

    span
      margin: $gap-small 0
      display: block
      font-family: $font-bold
      cursor: pointer

  .field-helper-text
    background: #fff
    position: absolute
    width: 290px
    height: 55px
    margin-left: 20px
    bottom: 0
    top: 0
    z-index: 10
    opacity: 0
    pointer-events: none
    transition: all .3s ease-in-out

    @include themify($themes)
      color: themed('color-body')

    p
      font-size: 10px
      margin: 0
      line-height: 1.6
</style>
